import { Helmet } from "react-helmet-async";
import TopBar from "@/components/TopBar";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { Phone, Clock, Zap, CheckCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

const ServiceUrgence = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Service d'urgence 24/7 - Agroref</title>
        <meta name="description" content="Service d'intervention d'urgence disponible 24h/24 et 7j/7 pour vos systèmes de traite" />
      </Helmet>
      
      <TopBar />
      <NavBar />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-accent/10 to-accent/5 py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <Zap className="h-16 w-16 text-accent mx-auto mb-6" />
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                Service d'urgence 24/7
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Une équipe d'intervention rapide disponible à tout moment pour résoudre vos problèmes urgents
              </p>
              <a
                href="tel:+15141234567"
                className="inline-flex items-center gap-2 bg-accent text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-accent/90 transition-colors"
              >
                <Phone className="h-6 w-6" />
                Appeler maintenant
              </a>
            </div>
          </div>
        </section>

        {/* Services Details */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-3 gap-8 mb-12">
                <Card>
                  <CardContent className="pt-6 text-center">
                    <Clock className="h-12 w-12 text-accent mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-3">24/7</h3>
                    <p className="text-muted-foreground">
                      Disponible jour et nuit, tous les jours de l'année
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6 text-center">
                    <Zap className="h-12 w-12 text-accent mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-3">Rapide</h3>
                    <p className="text-muted-foreground">
                      Intervention rapide pour minimiser les temps d'arrêt
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6 text-center">
                    <Phone className="h-12 w-12 text-accent mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-3">Expert</h3>
                    <p className="text-muted-foreground">
                      Techniciens expérimentés et équipés
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="bg-card rounded-lg p-8 shadow-sm mb-8">
                <h2 className="text-2xl font-bold mb-6">Situations d'urgence couvertes</h2>
                <div className="space-y-4">
                  {[
                    "Panne complète du système de traite",
                    "Problèmes de vide ou de pression",
                    "Défaillance du système de refroidissement",
                    "Problèmes électriques ou mécaniques critiques",
                    "Fuites importantes dans le circuit de lait",
                    "Dysfonctionnement des robots de traite",
                    "Problèmes de nettoyage automatique",
                    "Toute urgence affectant la traite ou la qualité du lait"
                  ].map((service, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-6 w-6 text-accent flex-shrink-0 mt-0.5" />
                      <p className="text-muted-foreground">{service}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-accent/10 rounded-lg p-8 border-2 border-accent/20">
                <h2 className="text-2xl font-bold mb-4 text-center">En cas d'urgence</h2>
                <div className="text-center">
                  <p className="text-lg text-muted-foreground mb-6">
                    N'attendez pas que la situation s'aggrave. Contactez-nous immédiatement :
                  </p>
                  <a
                    href="tel:+15141234567"
                    className="inline-flex items-center gap-3 bg-accent text-white px-10 py-5 rounded-lg font-bold text-2xl hover:bg-accent/90 transition-colors"
                  >
                    <Phone className="h-8 w-8" />
                    514-123-4567
                  </a>
                  <p className="text-sm text-muted-foreground mt-4">
                    Ligne d'urgence disponible 24h/24, 7j/7
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Reassurance Section */}
        <section className="bg-primary/5 py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-4">Votre tranquillité d'esprit</h2>
              <p className="text-lg text-muted-foreground">
                Avec notre service d'urgence 24/7, vous n'êtes jamais seul face à un problème. 
                Notre équipe d'experts est toujours prête à intervenir pour assurer la continuité 
                de votre production laitière.
              </p>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default ServiceUrgence;
